import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  IActivo,
  IFilter,
  IGeoJSONPoint,
  IQueryParam,
  IRecorrido,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription, retry, timer } from 'rxjs';
import {
  LineStringMapa,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapCardConfig } from '../../../../../componentes/map-card/map-card.component';
import { TonoEstado } from '../../../../../componentes/detalle/detalle.components';
import {
  EstadoConexion,
  duracion,
  estadoConexion,
  haceCuanto,
  minutosDesde,
} from '../../../../../auxiliares/estado-conexion.util';

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaColectivo {
  conexion: EstadoConexion;
  hace?: string;
  ultimoReporte?: string;
  /** dentroDelRecorrido; sin recorrido asignado es un texto. */
  recorrido: { tono?: TonoEstado; etiqueta: string };
  marcaModelo?: string;
  anio?: string;
  fotos: string[];
  tracker?: {
    id?: string;
    nombre?: string;
    tipo?: string;
    uniqueId?: string;
    frecuencia?: string;
  };
  ruta?: {
    nombre?: string;
    color?: string;
    destino?: string;
    por?: string;
    horarios: { dia: string; horario: string; frecuencia?: string }[];
  };
}

@Component({
  selector: 'app-detalles-colectivo-info',
  templateUrl: './detalles-colectivo-info.component.html',
  styleUrl: './detalles-colectivo-info.component.scss',
  standalone: false,
})
export class DetallesColectivoInfoComponent implements OnChanges, OnDestroy {
  private helper = inject(HelperService);
  private lista2 = inject(Lista2Service);

  /** Lo pide (y refresca) la página. */
  @Input() colectivo?: IActivo;

  public puedeVerTracker = this.helper.tienePermiso(
    'Administración - Ver trackers',
  );

  public vista?: VistaColectivo;
  /** El último reporte de cualquier tipo: conexión y "Último reporte". */
  private reporteReciente?: IReporteGenerico;
  /** El último con posición: el mapa. */
  private reportePosicion?: IReporteGenerico;
  private reportes$?: Subscription;
  private idPedido?: string;
  /** Lo que está dibujado en el mapa: se rehace solo si cambia. */
  private claveMapa?: string;

  public mapCardConfig: MapCardConfig = {
    title: 'Última ubicación',
    icon: 'location_on',
    color: 'red',
  };
  public loadingMapa = signal(true);

  public verFotos = false;
  public indiceFoto = 0;

  /** Ver `pantallaAncha` y `altoMapa` en el detalle de tracker. */
  public pantallaAncha = signal(window.innerWidth > 900);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 900);
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama con cada refresco del
   * colectivo (la página) y de los últimos reportes.
   */
  private armarVista(): void {
    const c = this.colectivo;
    if (!c) return;
    const t = c.tracker;
    const v = c.vehiculo;
    const r = v?.recorrido;

    // Conexión con el más nuevo de cualquier tipo, como tracker y vehículos.
    const fecha = this.reporteReciente?.fechaCreacion;

    this.vista = {
      conexion: t
        ? estadoConexion(minutosDesde(fecha), t.frecReporte)
        : { tono: 'neutro', etiqueta: 'Sin tracker' },
      hace: fecha ? haceCuanto(fecha) : undefined,
      ultimoReporte: fecha ? HelperService.getFechaYHora(fecha) : undefined,
      recorrido: !r
        ? { etiqueta: 'Sin recorrido asignado' }
        : v?.dentroDelRecorrido === true
          ? { tono: 'ok', etiqueta: 'Dentro del recorrido' }
          : v?.dentroDelRecorrido === false
            ? { tono: 'alerta', etiqueta: 'Fuera del recorrido' }
            : { tono: 'neutro', etiqueta: 'Sin información' },
      marcaModelo: [v?.marca, v?.modelo].filter(Boolean).join(' ') || undefined,
      anio: v?.anio,
      fotos: c.imagenes?.filter(Boolean) ?? [],
      tracker: t
        ? {
            id: t._id,
            nombre: t.nombre,
            tipo: t.tipo,
            uniqueId: t.uniqueId,
            frecuencia: t.frecReporte
              ? `cada ${duracion(t.frecReporte)}`
              : undefined,
          }
        : undefined,
      ruta: r
        ? {
            nombre: r.nombre,
            color: r.color,
            destino: r.destino,
            por: r.por,
            horarios: (r.franjaHoraria ?? []).map((f) => ({
              dia: this.helper.diaATexto(f.dia ?? 0),
              horario: `${this.helper.getHora(f.desde ?? '')} a ${this.helper.getHora(f.hasta ?? '')}`,
              frecuencia: f.frecuenciaMinutos
                ? `cada ${duracion(f.frecuenciaMinutos)}`
                : undefined,
            })),
          }
        : undefined,
    };
  }

  /**
   * Últimos reportes del colectivo, cada 30 s: hay uno por tipoReporte (y,
   * con un tracker cambiado, también los del anterior), del más nuevo al más
   * viejo.
   */
  private listarUltimosReportes(): void {
    this.reportes$?.unsubscribe();
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: this.colectivo?._id,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reportes$ = this.lista2
      .ultimosReportesGenericos(query, 30)
      // Un request que falla terminaba el refresco para siempre
      .pipe(retry({ delay: () => timer(30000) }))
      .subscribe((data) => {
        const reportes = data?.datos ?? [];
        this.reporteReciente = reportes[0];
        this.reportePosicion = reportes.find((r) => this.coordenadas(r));
        this.armarVista();
        this.armarMapa();
      });
  }

  private coordenadas(r?: IReporteGenerico): [number, number] | undefined {
    const geojson = (r?.valores as { geojson?: IGeoJSONPoint } | undefined)
      ?.geojson;
    const c = geojson?.coordinates;
    return c?.length === 2 ? [c[0], c[1]] : undefined;
  }

  /**
   * El colectivo (orientado) y su recorrido. Se rehace solo si cambió la
   * posición o el recorrido: con cada refresco el mapa parpadeaba.
   */
  private armarMapa(): void {
    const coordenadas = this.coordenadas(this.reportePosicion);
    const recorrido = this.colectivo?.vehiculo?.recorrido;
    const valores = this.reportePosicion?.valores as
      | { orientacion?: number; direccion?: string }
      | undefined;
    const clave = [
      coordenadas?.join(),
      valores?.orientacion,
      recorrido?._id,
    ].join('|');
    if (clave === this.claveMapa) return;
    this.claveMapa = clave;

    if (!coordenadas) {
      this.mapCardConfig = {
        title: 'Última ubicación',
        icon: 'location_on',
        color: 'red',
        mapOptions: undefined,
      };
      this.loadingMapa.set(false);
      return;
    }
    const marker: MarkerMapa = {
      label: 'colectivo', // ícono de colectivo con la orientación
      coor: coordenadas,
      centrar: true,
      orientation: valores?.orientacion,
      data: this.reportePosicion,
    };
    const linea = recorrido?.geojson?.coordinates
      ? this.lineaRecorrido(recorrido)
      : undefined;
    this.mapCardConfig = {
      title: 'Última ubicación',
      subtitle: HelperService.extraerDireccion(valores?.direccion),
      icon: 'location_on',
      color: 'red',
      mapOptions: {
        markers: [marker],
        polylines: linea ? [linea] : undefined,
        center: coordenadas,
        zoom: 17,
      },
    };
    this.loadingMapa.set(false);
  }

  private lineaRecorrido(r: IRecorrido): LineStringMapa {
    return {
      points: r.geojson.coordinates.map(
        (coord) => [coord[0], coord[1]] as [number, number],
      ),
      color: 'blue',
      width: 5,
      arrow: true,
      centrar: false,
    };
  }

  public abrirFoto(indice: number): void {
    this.indiceFoto = indice;
    this.verFotos = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['colectivo']) return;
    // El mismo colectivo refrescado por la página: se rearma la vista y el
    // mapa (por si cambió el recorrido), sin volver a pedir los reportes.
    if (this.colectivo?._id === this.idPedido) {
      this.armarVista();
      if (!this.loadingMapa()) this.armarMapa();
      return;
    }
    this.idPedido = this.colectivo?._id;
    this.reporteReciente = undefined;
    this.reportePosicion = undefined;
    this.claveMapa = undefined;
    this.loadingMapa.set(true);
    this.armarVista();
    if (this.colectivo?._id) this.listarUltimosReportes();
  }

  ngOnDestroy(): void {
    this.reportes$?.unsubscribe();
    this.observadorMapa?.disconnect();
  }
}
